import { useEffect } from 'react';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  size: string;
}

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  subtotal: number;
  shipping: number;
  total: number;
}

export default function CartDrawer({ 
  isOpen, 
  onClose, 
  items, 
  onUpdateQuantity, 
  onRemoveItem,
  subtotal,
  shipping,
  total 
}: CartDrawerProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl transform transition-transform duration-300 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-sage-200">
          <h2 className="font-serif text-2xl font-semibold text-sage-600">Your Cart</h2>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-sage-50 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-sage-50 rounded-full">
                <i className="ri-shopping-cart-line text-4xl text-sage-400"></i>
              </div>
              <p className="font-sans text-gray-500 mb-2">Your cart is empty</p>
              <p className="font-sans text-sm text-gray-400">Add some products to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 p-4 bg-cream-50 rounded-2xl border border-sage-200">
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                    <img 
                      alt={item.name} 
                      className="w-full h-full object-cover object-top" 
                      src={item.image}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif font-semibold text-base text-gray-800 mb-1 leading-tight">
                      {item.name}
                    </h3>
                    <p className="text-xs font-sans text-gray-500 mb-2">{item.size}</p>
                    <p className="font-sans text-sm font-semibold text-sage-600 mb-3">
                      ${item.price.toFixed(2)}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 bg-white rounded-lg border border-sage-300">
                        <button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-sage-50 transition-colors cursor-pointer rounded-l-lg"
                        >
                          <i className="ri-subtract-line text-sm text-gray-600"></i>
                        </button>
                        <span className="font-sans text-sm font-medium text-gray-800 min-w-[20px] text-center">
                          {item.quantity}
                        </span>
                        <button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-sage-50 transition-colors cursor-pointer rounded-r-lg"
                        >
                          <i className="ri-add-line text-sm text-gray-600"></i>
                        </button>
                      </div>
                      
                      {/* Remove Button */}
                      <button 
                        onClick={() => onRemoveItem(item.id)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-base text-red-500"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Totals */}
        {items.length > 0 && (
          <div className="border-t border-sage-200 p-6 bg-sage-50">
            <div className="space-y-3 mb-5">
              <div className="flex items-center justify-between font-sans text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-800">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between font-sans text-sm">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium text-gray-800">${shipping.toFixed(2)}</span>
              </div>
              <div className="h-px bg-sage-300"></div>
              <div className="flex items-center justify-between font-serif text-lg">
                <span className="font-semibold text-sage-600">Total</span>
                <span className="font-semibold text-sage-600">${total.toFixed(2)}</span>
              </div>
            </div>
            
            <button className="w-full py-4 bg-sage-500 text-cream-100 rounded-xl font-sans text-base font-medium hover:bg-sage-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </>
  );
}